/**
 * Cart Delivery Validator
 *
 * Validates and corrects delivery info (Verfügbarkeit property) for ALL cart items.
 * - Runs on page load (delayed, non-blocking)
 * - Blocks checkout until validation passes (ensures correct delivery text)
 * - Listens for cart:updated events to re-validate
 *
 * Uses StorefrontDeliveryHelper for authoritative delivery data via Storefront API.
 */
(function() {
  'use strict';

  var PAGE_LOAD_DELAY = 3000;
  var MIN_REVALIDATION_INTERVAL = 30000;
  var HELPER_WAIT_TIMEOUT = 10000;
  var CHECKOUT_VALIDATION_TIMEOUT = 15000;
  var SKIP_VARIANT_ID = 32206654079059;
  var FAST_DELIVERY_TEXT = 'Auf Lager. Lieferzeit 1-4 Werktage';
  var DEFAULT_SLOW_TEXT = 'Lieferzeit ca. 4 - 6 Wochen';

  var isValidating = false;
  var lastValidationTime = 0;

  // ==================== Utilities ====================

  function waitForHelper() {
    return new Promise(function(resolve, reject) {
      if (window.StorefrontDeliveryHelper) {
        resolve();
        return;
      }
      var startTime = Date.now();
      var interval = setInterval(function() {
        if (window.StorefrontDeliveryHelper) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() - startTime > HELPER_WAIT_TIMEOUT) {
          clearInterval(interval);
          reject(new Error('StorefrontDeliveryHelper not loaded'));
        }
      }, 200);
    });
  }

  function getCart() {
    return fetch('/cart.js').then(function(res) { return res.json(); });
  }

  function updateCartItemByKey(itemKey, quantity, properties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: quantity, properties: properties })
    }).then(function(res) { return res.json(); });
  }

  // ==================== Core Validation ====================

  /**
   * Groups cart items by product handle for batched API calls.
   * Sums quantities per variant to check against inventory.
   */
  function groupCartItemsByHandle(items) {
    var groups = {};

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.variant_id == SKIP_VARIANT_ID) continue;
      var handle = item.handle;
      if (!handle) continue;

      if (!groups[handle]) {
        groups[handle] = { variants: {} };
      }

      var variantId = String(item.variant_id);
      if (!groups[handle].variants[variantId]) {
        groups[handle].variants[variantId] = { totalQty: 0, lineItems: [] };
      }

      groups[handle].variants[variantId].totalQty += item.quantity;
      groups[handle].variants[variantId].lineItems.push({
        key: item.key,
        quantity: item.quantity,
        properties: item.properties || {},
        variantId: variantId
      });
    }

    return groups;
  }

  /**
   * Validates a product group against Storefront API data.
   * Returns array of items that need property updates.
   */
  function validateProductGroup(handle, variantGroup) {
    return window.StorefrontDeliveryHelper.fetchProductDeliveryData(handle)
      .then(function(productData) {
        var updates = [];

        if (!productData) return updates;

        var metafield = productData.deliveryTimeMetafield;

        var variantIds = Object.keys(variantGroup.variants);
        for (var v = 0; v < variantIds.length; v++) {
          var variantId = variantIds[v];
          var group = variantGroup.variants[variantId];
          var variantData = productData.variants[variantId];

          var correctText;

          if (!variantData) {
            correctText = metafield || DEFAULT_SLOW_TEXT;
          } else {
            var inStock = variantData.quantityAvailable > 0;
            var totalExceedsStock = group.totalQty > variantData.quantityAvailable;
            var canOversell = variantData.availableForSale && variantData.currentlyNotInStock;

            if (inStock && !totalExceedsStock) {
              correctText = FAST_DELIVERY_TEXT;
            } else if ((inStock && totalExceedsStock) || canOversell) {
              correctText = metafield || DEFAULT_SLOW_TEXT;
            } else if (!variantData.availableForSale) {
              correctText = 'Nicht verfügbar';
            } else {
              correctText = FAST_DELIVERY_TEXT;
            }
          }

          for (var j = 0; j < group.lineItems.length; j++) {
            var lineItem = group.lineItems[j];
            var currentText = lineItem.properties['Verfügbarkeit'] || '';

            if (currentText !== correctText) {
              var newProps = {};
              var keys = Object.keys(lineItem.properties);
              for (var k = 0; k < keys.length; k++) {
                newProps[keys[k]] = lineItem.properties[keys[k]];
              }
              newProps['Verfügbarkeit'] = correctText;

              updates.push({
                key: lineItem.key,
                quantity: lineItem.quantity,
                newProperties: newProps,
                oldText: currentText,
                newText: correctText
              });
            }
          }
        }

        return updates;
      })
      .catch(function() {
        return [];
      });
  }

  /**
   * Main validation function.
   * @param {Object} options - { force: bool, clearCache: bool }
   */
  function validateCart(options) {
    options = options || {};

    if (isValidating) {
      return Promise.resolve({ updated: 0, checked: 0, skipped: true });
    }

    if (!options.force && (Date.now() - lastValidationTime < MIN_REVALIDATION_INTERVAL)) {
      return Promise.resolve({ updated: 0, checked: 0, skipped: true });
    }

    isValidating = true;
    var result = { updated: 0, checked: 0, errors: 0, skipped: false };

    return waitForHelper()
      .then(function() {
        if (options.clearCache) {
          window.StorefrontDeliveryHelper.clearCache();
        }
        return getCart();
      })
      .then(function(cart) {
        if (!cart.items || cart.items.length === 0) {
          return result;
        }

        var groups = groupCartItemsByHandle(cart.items);
        var handles = Object.keys(groups);
        if (handles.length === 0) return result;

        result.checked = cart.items.length;

        // Validate in batches of 3
        var allUpdates = [];
        var BATCH_SIZE = 3;

        function processBatch(startIdx) {
          if (startIdx >= handles.length) return Promise.resolve();

          var batch = handles.slice(startIdx, startIdx + BATCH_SIZE);
          var batchPromises = batch.map(function(handle) {
            return validateProductGroup(handle, groups[handle]);
          });

          return Promise.all(batchPromises).then(function(batchResults) {
            for (var b = 0; b < batchResults.length; b++) {
              allUpdates = allUpdates.concat(batchResults[b]);
            }
            return processBatch(startIdx + BATCH_SIZE);
          });
        }

        return processBatch(0).then(function() {
          if (allUpdates.length === 0) {
            lastValidationTime = Date.now();
            return result;
          }

          // Apply updates sequentially
          var updatePromise = Promise.resolve();
          allUpdates.forEach(function(update) {
            updatePromise = updatePromise.then(function() {
              return updateCartItemByKey(update.key, update.quantity, update.newProperties)
                .then(function() { result.updated++; })
                .catch(function() { result.errors++; });
            });
          });

          return updatePromise.then(function() {
            if (result.updated > 0) {
              refreshCartView();
            }
            lastValidationTime = Date.now();
            return result;
          });
        });
      })
      .catch(function(err) {
        result.errors++;
        return result;
      })
      .then(function(res) {
        isValidating = false;
        return res;
      });
  }

  // ==================== Cart View Refresh ====================

  function refreshCartView() {
    try {
      if (window.PXUTheme && window.PXUTheme.jsAjaxCart &&
          typeof window.PXUTheme.jsAjaxCart.updateView === 'function') {
        window.PXUTheme.jsAjaxCart.updateView();
      }
    } catch (e) {}
  }

  // ==================== Checkout Interception ====================

  function showCheckoutLoading(btn) {
    if (btn) {
      btn.disabled = true;
      btn.style.opacity = '0.6';
      btn.style.pointerEvents = 'none';
    }
  }

  function hideCheckoutLoading(btn) {
    if (btn) {
      btn.disabled = false;
      btn.style.opacity = '';
      btn.style.pointerEvents = '';
    }
  }

  function submitFormAfterValidation(form) {
    form.setAttribute('data-delivery-validated', 'true');
    var hiddenSubmit = document.createElement('button');
    hiddenSubmit.type = 'submit';
    hiddenSubmit.name = 'checkout';
    hiddenSubmit.style.display = 'none';
    form.appendChild(hiddenSubmit);
    hiddenSubmit.click();
    form.removeChild(hiddenSubmit);
  }

  function handleCheckoutIntercept(e) {
    var form = e.target.closest ? e.target.closest('form') : e.target;
    if (!form || form.tagName !== 'FORM') return;

    var hasCheckoutButton = form.querySelector('[name="checkout"]');
    if (!hasCheckoutButton && !e.target.matches('[name="checkout"]')) return;

    // Skip if already validated
    if (form.getAttribute('data-delivery-validated') === 'true') {
      form.removeAttribute('data-delivery-validated');
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    var checkoutBtn = form.querySelector('[name="checkout"]') || e.target;
    showCheckoutLoading(checkoutBtn);

    Promise.race([
      validateCart({ force: true, clearCache: true }),
      new Promise(function(_, reject) {
        setTimeout(function() { reject(new Error('timeout')); }, CHECKOUT_VALIDATION_TIMEOUT);
      })
    ])
    .then(function() {
      hideCheckoutLoading(checkoutBtn);
      submitFormAfterValidation(form);
    })
    .catch(function() {
      // Don't block checkout on validation error
      hideCheckoutLoading(checkoutBtn);
      submitFormAfterValidation(form);
    });
  }

  function installCheckoutInterceptors() {
    document.addEventListener('submit', function(e) {
      if (e.target.tagName !== 'FORM') return;
      if (e.target.getAttribute('data-delivery-validated') === 'true') {
        e.target.removeAttribute('data-delivery-validated');
        return;
      }
      var hasCheckout = e.target.querySelector('[name="checkout"]');
      if (hasCheckout) {
        handleCheckoutIntercept(e);
      }
    }, true);

    document.addEventListener('click', function(e) {
      var btn = e.target.closest ? e.target.closest('[name="checkout"][type="submit"]') : null;
      if (!btn) return;
      var form = btn.closest('form');
      if (!form) return;
      if (form.getAttribute('data-delivery-validated') === 'true') return;
      handleCheckoutIntercept(e);
    }, true);
  }

  // ==================== Page Load & Events ====================

  function runPageLoadValidation() {
    var cartCountEl = document.querySelector('[data-bind="itemCount"]');
    var countText = cartCountEl ? cartCountEl.textContent.trim() : '0';
    var count = parseInt(countText, 10);

    if (count > 0) {
      validateCart({ force: false, clearCache: false });
    }
  }

  function listenForCartChanges() {
    document.addEventListener('cart:updated', function() {
      setTimeout(function() {
        validateCart({ force: true, clearCache: false });
      }, 2000);
    });
  }

  // ==================== Init ====================

  function init() {
    installCheckoutInterceptors();
    listenForCartChanges();

    if (document.readyState === 'complete') {
      setTimeout(runPageLoadValidation, PAGE_LOAD_DELAY);
    } else {
      window.addEventListener('load', function() {
        setTimeout(runPageLoadValidation, PAGE_LOAD_DELAY);
      });
    }
  }

  window.CartDeliveryValidator = {
    validate: validateCart,
    isValidating: function() { return isValidating; }
  };

  init();
})();
